/**
 * @file useUnifiedAuth.js
 * @brief Provides a single auth interface that bridges OIDC and local token authentication.
 */
import { useAuth } from "react-oidc-context";
import { useLocalAuth } from "../context/AuthContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useCallback, useMemo } from "react";

/**
 * Unified auth hook that works with both OIDC and local auth.
 * Returns a consistent interface regardless of which auth method is active.
 */
export function useUnifiedAuth() {
    const oidc = useAuth();
    const local = useLocalAuth();
    const { siteSettings } = useSiteSettings();

    const ssoEnabled = siteSettings?.auth_sso_enabled !== false;
    const localEnabled = siteSettings?.auth_local_enabled === true;

    // Local auth takes precedence when the user is logged in locally
    const isLocalAuth = Boolean(local?.isAuthenticated);
    const isOidcAuth = Boolean(!isLocalAuth && oidc?.isAuthenticated);
    const isAuthenticated = isLocalAuth || isOidcAuth;
    const isLoading = (!local?.ready) || (ssoEnabled && oidc.isLoading && !oidc.isAuthenticated && !isLocalAuth);

    const accessToken = isLocalAuth
        ? local.token
        : oidc.user?.access_token || null;

    const user = useMemo(() => {
        if (isLocalAuth) {
            return { access_token: local.token };
        }
        if (isOidcAuth) {
            return oidc.user;
        }
        return null;
    }, [isLocalAuth, isOidcAuth, local?.token, oidc.user]);

    const signinRedirect = useCallback(() => {
        if (ssoEnabled) {
            oidc.signinRedirect();
        }
    }, [ssoEnabled, oidc]);

    const logout = useCallback(() => {
        if (isLocalAuth) {
            local.logout();
        } else if (isOidcAuth) {
            oidc.signoutRedirect();
        }
    }, [isLocalAuth, isOidcAuth, local, oidc]);

    return {
        isAuthenticated,
        isLoading,
        isLocalAuth,
        isOidcAuth,
        user,
        accessToken,
        signinRedirect,
        logout,
        ssoEnabled,
        localEnabled,
        // Expose the underlying contexts for edge cases
        oidc,
        local,
        // Pass-through for OIDC event handling
        events: oidc.events,
        error: isLocalAuth ? null : oidc.error,
    };
}
